// services/ProjectFileService.js
import { projectCachedAxios } from '../../../shared/config/cachedAxiosConfig';
import { PROJECT_ENDPOINTS, FILE_ENDPOINTS } from '../config/projectApiEndpoints';
import { getAuthHeaders } from '../../../shared/services/auth/authHeaders';
import { ProjectHeadersService } from './ProjectHeadersService';
import logger from '../../services/logging/logger';

// Request deduplication - tracks in-flight requests to prevent duplicates
const inflightRequests = new Map();

export const ProjectFileService = {
  /**
   * Get all projects (updated to support options and match dashboard behavior)
   * Now includes request deduplication to prevent duplicate simultaneous calls
   */
  getAllProjects: async (userData, options = {}) => {
    // TEMPORARY: set REACT_APP_DISABLE_PROJECTS=true in .env to skip this call during local
    // testing that doesn't need Projects (e.g. local backend not yet returning valid JWTs).
    // Remove REACT_APP_DISABLE_PROJECTS from .env to re-enable.
    if (process.env.REACT_APP_DISABLE_PROJECTS === 'true') {
      return [];
    }

    // Generate cache key for deduplication
    const cacheKey = `projects-${userData?.organizationId || 'default'}-${userData?.userId || 'anonymous'}`;

    // Check if there's already an in-flight request for this data
    if (inflightRequests.has(cacheKey)) {
      logger.debug('Deduplicating getAllProjects request (returning existing promise)');
      return inflightRequests.get(cacheKey);
    }

    try {
      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);

      // Add user information to headers for permission validation
      if (userData && userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }

      // Handle both single groupId and multiple groupIds
      if (userData && userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData && userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }

      // Create request config with optional abort signal
      const requestConfig = { headers };
      if (options.signal) {
        // requestConfig.signal = options.signal;
      }

      const fullUrl = PROJECT_ENDPOINTS.getAll;
      Object.keys(headers).forEach(key => {
      });

      // Create and track the request promise
      const requestPromise = (async () => {
        try {
          const response = await projectCachedAxios.get(fullUrl, requestConfig);
          logger.info('getAllProjects request completed');
          return response.data;
        } finally {
          // Clean up in-flight tracking after request completes (success or failure)
          inflightRequests.delete(cacheKey);
        }
      })();

      // Store the promise to deduplicate simultaneous requests
      inflightRequests.set(cacheKey, requestPromise);

      return requestPromise;
    } catch (error) {

      if (error.response) {
      } else if (error.request) {
      } else {
      }

      throw error;
    }
  },

  /**
   * Get files/folders for a specific project
   * Now includes request deduplication to prevent duplicate simultaneous calls
   */
  getProjectFiles: async (projectId, path = '', userData) => {
    // Generate cache key for deduplication
    const cacheKey = `files-${projectId}-${path}-${userData?.userId || 'anonymous'}`;

    // Check if there's already an in-flight request for this data
    if (inflightRequests.has(cacheKey)) {
      logger.debug('Deduplicating getProjectFiles request (returning existing promise)');
      return inflightRequests.get(cacheKey);
    }

    try {
      if (!projectId) {
        throw new Error('Missing required parameter: projectId');
      }

      const baseHeaders = await getAuthHeaders();
      const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);

      // Add user information to headers for permission validation
      if (userData && userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }

      if (userData && userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData && userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      } else {
      }

      // Create and track the request promise
      const requestPromise = (async () => {
        try {
          const response = await projectCachedAxios.get(
            FILE_ENDPOINTS.getFiles(projectId, path),
            { headers }
          );
          logger.info('getProjectFiles request completed');
          return response.data;
        } finally {
          // Clean up in-flight tracking after request completes
          inflightRequests.delete(cacheKey);
        }
      })();

      // Store the promise to deduplicate simultaneous requests
      inflightRequests.set(cacheKey, requestPromise);

      return requestPromise;
    } catch (error) {
      if (error.response) {
      }
      throw error;
    }
  },

  /**
   * Get folders for a specific project (filters out files, returns only directories)
   */
  getProjectFolders: async (projectId, path = '', userData) => {
    try {
      const files = await ProjectFileService.getProjectFiles(projectId, path, userData);
      // Filter to only return directories
      return files.filter(item => item.directory === true);
    } catch (error) {
      throw error;
    }
  }
};